import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox, CheckboxField, CheckboxGroup } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog'
import { Description, Field as FormField, FieldGroup, Fieldset, Label, Legend } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Radio, RadioField, RadioGroup } from '../catalyst/radio'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useTrainingSessions, type TrainingSessionInput } from '../lib/farm-level-logs'
import { byDateDesc, FarmLogLayout, orNull, text, useSavedEntries, type DialogState } from '../lib/farm-log-layout'
import type { TrainingRecordType, TrainingSession, TrainingTopic } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Training Session'

const TOPICS: { value: TrainingTopic; label: string; description: string }[] = [
  { value: 'form_c', label: 'Form C', description: 'Employee personal hygiene and food handling policy — production site' },
  { value: 'form_d', label: 'Form D', description: 'Employee personal hygiene and food handling policy — packinghouse / storage' },
  { value: 'deviations', label: 'Minor and major deviations', description: 'What to do when something goes wrong' },
  { value: 'other', label: 'Other', description: 'Describe the topic below' },
]
const topicLabel = (t: TrainingTopic) => TOPICS.find((x) => x.value === t)!.label

const RECORD_TYPES: { value: TrainingRecordType; label: string }[] = [
  { value: 'casual', label: 'Casual employee (C)' },
  { value: 'contract', label: 'Contract employee (CE)' },
  { value: 'payroll', label: 'Payroll record (P)' },
  { value: 'signature', label: 'Employee signatures' },
]

function TrainingSessionDialog({
  onClose,
  initial,
  onSave,
}: {
  onClose: () => void
  initial?: TrainingSession
  onSave: (input: TrainingSessionInput) => Promise<unknown>
}) {
  const [heldOn, setHeldOn] = useState(initial?.heldOn ?? '')
  const [topics, setTopics] = useState<TrainingTopic[]>(initial?.topics ?? [])
  const [topicOther, setTopicOther] = useState(text(initial?.topicOther))
  const [trainees, setTrainees] = useState<'count' | 'names'>(initial?.traineeNames ? 'names' : 'count')
  const [traineeCount, setTraineeCount] = useState(initial?.traineeCount == null ? '' : String(initial.traineeCount))
  const [traineeNames, setTraineeNames] = useState(text(initial?.traineeNames))
  const [trainerName, setTrainerName] = useState(text(initial?.trainerName))
  const [recordType, setRecordType] = useState<TrainingRecordType | ''>(initial?.recordType ?? '')
  const [recordReference, setRecordReference] = useState(text(initial?.recordReference))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleTopic = (topic: TrainingTopic, on: boolean) =>
    setTopics((prev) => (on ? [...prev, topic] : prev.filter((t) => t !== topic)))

  // Everything but the date, as saved; also what the CanadaGAP check runs on.
  const fields = {
    topics: TOPICS.map((t) => t.value).filter((t) => topics.includes(t)),
    topicOther: topics.includes('other') ? orNull(topicOther) : null,
    traineeCount: trainees === 'count' && traineeCount ? Number(traineeCount) : null,
    traineeNames: trainees === 'names' ? orNull(traineeNames) : null,
    trainerName: orNull(trainerName),
    recordType: recordType || null,
    recordReference: orNull(recordReference),
  }
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const check = useEntryCheck(org?.id, farmId, { form: 'k', entry: { heldOn: heldOn || null, ...fields } })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!heldOn) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({ heldOn, ...fields })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save training session.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} size="2xl">
      <DialogTitle>{initial ? 'Edit training session' : 'Record a training session'}</DialogTitle>
      <DialogDescription>For all employees handling product, packaging materials or food contact surfaces.</DialogDescription>
      <form onSubmit={handleSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="K" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <FormField>
              <Label>Date</Label>
              <Input type="date" value={heldOn} onChange={(e) => setHeldOn(e.target.value)} required autoFocus />
            </FormField>

            <Fieldset>
              <Legend>Topic covered</Legend>
              <CheckboxGroup>
                {TOPICS.map((t) => (
                  <CheckboxField key={t.value}>
                    <Checkbox checked={topics.includes(t.value)} onChange={(on) => toggleTopic(t.value, on)} />
                    <Label>{t.label}</Label>
                    <Description>{t.description}</Description>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
              <InputWarning result={check} input="topics" />
            </Fieldset>
            {topics.includes('other') && (
              <FormField>
                <Label>Other topic</Label>
                <Input value={topicOther} onChange={(e) => setTopicOther(e.target.value)} placeholder="e.g. Heat stress and first aid" />
                <InputWarning result={check} input="topicOther" />
              </FormField>
            )}

            <Fieldset>
              <Legend>Employees trained</Legend>
              <RadioGroup value={trainees} onChange={(v) => setTrainees(v as 'count' | 'names')}>
                <RadioField>
                  <Radio value="count" />
                  <Label>Number of employees</Label>
                </RadioField>
                <RadioField>
                  <Radio value="names" />
                  <Label>Employee names</Label>
                </RadioField>
              </RadioGroup>
            </Fieldset>
            {trainees === 'count' ? (
              <FormField>
                <Label>Number trained</Label>
                <Input type="number" min="1" step="1" value={traineeCount} onChange={(e) => setTraineeCount(e.target.value)} />
              </FormField>
            ) : (
              <FormField>
                <Label>Names</Label>
                <Textarea rows={2} value={traineeNames} onChange={(e) => setTraineeNames(e.target.value)} placeholder="Comma separated" />
              </FormField>
            )}

            <FormField>
              <Label>Person responsible for training</Label>
              <Input value={trainerName} onChange={(e) => setTrainerName(e.target.value)} />
              <InputWarning result={check} input="trainerName" />
            </FormField>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField>
                <Label>Where the attendance record is</Label>
                <Select value={recordType} onChange={(e) => setRecordType(e.target.value as TrainingRecordType | '')}>
                  <option value="">— select —</option>
                  {RECORD_TYPES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField>
                <Label>Record reference</Label>
                <Input value={recordReference} onChange={(e) => setRecordReference(e.target.value)} placeholder="e.g. Contractor sign-in sheet" />
              </FormField>
            </div>
          </FieldGroup>
          <EntryCheckPanel result={check} formCode="K" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="K" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function TrainingSessionsPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [dialog, setDialog] = useState<DialogState<TrainingSession>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLog, updateLog } = useTrainingSessions(org?.id, farmId, year)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)

  async function save(input: TrainingSessionInput) {
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog(input)
    await reloadStatus()
    if (log) saved.setIds([log.id])
  }

  const entries = [...logs].sort(byDateDesc((r) => r.heldOn)).map((log) => ({
    key: log.id,
    date: log.heldOn,
    onClick: () => setDialog({ log }),
    content: (
      <>
        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
          {log.topics.map((t) => (t === 'other' && log.topicOther ? log.topicOther : topicLabel(t))).join(', ') || 'No topic recorded'}
        </p>
        <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
          {log.traineeNames ?? (log.traineeCount != null ? `${log.traineeCount} employees` : 'Trainees not recorded')}
          {log.trainerName && <> · trained by {log.trainerName}</>}
        </p>
        <EntryIssues issue={issueFor(status?.forms.k, log.id)} />
      </>
    ),
  }))

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="k"
      title="Training sessions"
      formName={FORM_NAME}
      addLabel="Record training"
      addHref={`/farms/${farmId}/log/training`}
      year={year}
      onYearChange={setYear}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No training sessions in ${year}.`}
      saved={saved}
    >
      {dialog && <TrainingSessionDialog key={dialog.log?.id ?? 'new'} onClose={() => setDialog(null)} initial={dialog.log} onSave={save} />}
    </FarmLogLayout>
  )
}
